import React, { useState } from "react";
import { View, StyleSheet, TextInput, Platform } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import type { RateEditorProps, BillingRateType, PayrollRateType } from "./types";

// Format billing rate for display
function formatBillingRate(rate: BillingRateType | null | undefined): string {
  if (!rate) return "-";
  if (rate.rate === "flat") return `$${rate.amount} flat`;
  return `$${rate.amount}/${rate.rate === "hourly" ? "hr" : "day"}`;
}

// Format payroll rate for display
function formatPayrollRate(rate: PayrollRateType | null | undefined): string {
  if (!rate) return "-";
  if (rate.rate === "base_rate") return `${rate.multiplier}x base (legacy)`;
  if (rate.rate === "base_add") return `+$${rate.offset}/hr`;
  if (rate.rate === "flat") return `$${(rate as any).amount} flat`;
  return `$${(rate as any).amount}/${rate.rate === "hourly" ? "hr" : "day"}`;
}

export function RateEditor({ value, onSave, onCancel }: RateEditorProps) {
  const { colors } = useTheme();

  // Extract values from the value prop
  const billingRate = value?.billing_rate || null;
  const payrollRate = value?.payroll_rate || null;
  const billingRateSnapshot = value?.billing_rate_snapshot || null;
  const payrollRateSnapshot = value?.payroll_rate_snapshot || null;
  const userPayRate = value?.user_pay_rate || null; // User's hourly rate for context

  // Override state - flat rate amounts
  const [billingOverride, setBillingOverride] = useState<string>(() => {
    // If current rate is flat (override), show it
    if (billingRate?.rate === "flat") {
      return billingRate.amount.toString();
    }
    return "";
  });

  const [payrollOverride, setPayrollOverride] = useState<string>(() => {
    // If current rate is flat (override), show it
    if (payrollRate?.rate === "flat") {
      return (payrollRate as any).amount.toString();
    }
    return "";
  });

  // Focus state for inputs
  const [billingFocused, setBillingFocused] = useState(false);
  const [payrollFocused, setPayrollFocused] = useState(false);

  // Check if overrides are active (non-empty input means modified)
  const billingModified = billingOverride.trim() !== "";
  const payrollModified = payrollOverride.trim() !== "";

  const handleRevertBilling = () => {
    setBillingOverride("");
  };

  const handleRevertPayroll = () => {
    setPayrollOverride("");
  };

  const handleApply = () => {
    // Build overrides - null means "use snapshot" (no override)
    const newBillingRate = billingOverride.trim()
      ? { rate: "flat" as const, amount: parseFloat(billingOverride) }
      : null; // null = remove override, use snapshot

    const newPayrollRate = payrollOverride.trim()
      ? { rate: "flat" as const, amount: parseFloat(payrollOverride) }
      : null; // null = remove override, use snapshot

    onSave({
      billing_rate: newBillingRate,
      payroll_rate: newPayrollRate,
    });
  };

  return (
    <View style={styles.container}>
      <ThemedText type="subtitle">Rate Override</ThemedText>

      {/* Billing Rate Section */}
      <View style={styles.section}>
        <View style={styles.labelRow}>
          <ThemedText style={styles.label}>Billing Rate</ThemedText>
          {billingModified && (
            <Button
              text="Revert"
              icon="undo"
              iconSize={12}
              style={[styles.revertButton, { backgroundColor: colors.amber.background }]}
              textStyle={[styles.revertButtonText, { color: colors.amber.text }]}
              iconStyle={{ color: colors.amber.text }}
              onPress={handleRevertBilling}
            />
          )}
        </View>

        {/* Original value display */}
        <View style={styles.originalRow}>
          <ThemedText style={[styles.originalLabel, { color: colors.placeholder }]}>Original:</ThemedText>
          <ThemedText style={[styles.originalValue, { color: colors.text }]}>
            {formatBillingRate(billingRateSnapshot)}
          </ThemedText>
        </View>

        {/* Flat rate override input */}
        <View style={styles.overrideRow}>
          <ThemedText style={[styles.overrideLabel, { color: colors.placeholder }]}>Override:</ThemedText>
          <View
            style={[
              styles.inputWrapper,
              {
                borderColor: billingFocused ? colors.primary : billingModified ? colors.amber.border : colors.border,
              },
            ]}
          >
            <ThemedText style={{ color: colors.placeholder }}>$</ThemedText>
            <TextInput
              style={[styles.input, { color: colors.text }]}
              value={billingOverride}
              onChangeText={setBillingOverride}
              keyboardType="decimal-pad"
              placeholder="0.00"
              placeholderTextColor={colors.placeholder}
              onFocus={() => setBillingFocused(true)}
              onBlur={() => setBillingFocused(false)}
            />
            <ThemedText style={[styles.flatLabel, { color: colors.placeholder }]}>flat</ThemedText>
          </View>
        </View>
      </View>

      {/* Payroll Rate Section */}
      <View style={styles.section}>
        <View style={styles.labelRow}>
          <ThemedText style={styles.label}>Payroll Rate</ThemedText>
          {payrollModified && (
            <Button
              text="Revert"
              icon="undo"
              iconSize={12}
              style={[styles.revertButton, { backgroundColor: colors.amber.background }]}
              textStyle={[styles.revertButtonText, { color: colors.amber.text }]}
              iconStyle={{ color: colors.amber.text }}
              onPress={handleRevertPayroll}
            />
          )}
        </View>

        {/* Position payroll formula */}
        <View style={styles.originalRow}>
          <ThemedText style={[styles.originalLabel, { color: colors.placeholder }]}>Position:</ThemedText>
          <ThemedText style={[styles.originalValue, { color: colors.text }]}>
            {formatPayrollRate(payrollRateSnapshot)}
          </ThemedText>
        </View>

        {/* User's pay rate (what gets calculated) */}
        {userPayRate && (
          <View style={styles.originalRow}>
            <ThemedText style={[styles.originalLabel, { color: colors.placeholder }]}>User Pay:</ThemedText>
            <ThemedText style={[styles.originalValue, { color: colors.text }]}>${userPayRate}/hr</ThemedText>
          </View>
        )}

        {/* Flat rate override input */}
        <View style={styles.overrideRow}>
          <ThemedText style={[styles.overrideLabel, { color: colors.placeholder }]}>Override:</ThemedText>
          <View
            style={[
              styles.inputWrapper,
              {
                borderColor: payrollFocused ? colors.primary : payrollModified ? colors.amber.border : colors.border,
              },
            ]}
          >
            <ThemedText style={{ color: colors.placeholder }}>$</ThemedText>
            <TextInput
              style={[styles.input, { color: colors.text }]}
              value={payrollOverride}
              onChangeText={setPayrollOverride}
              keyboardType="decimal-pad"
              placeholder="0.00"
              placeholderTextColor={colors.placeholder}
              onFocus={() => setPayrollFocused(true)}
              onBlur={() => setPayrollFocused(false)}
            />
            <ThemedText style={[styles.flatLabel, { color: colors.placeholder }]}>flat</ThemedText>
          </View>
        </View>
      </View>

      {/* Footer Buttons */}
      <View style={styles.footer}>
        <Button
          text="Cancel"
          style={[styles.cancelButton, { borderColor: colors.border }]}
          textStyle={{ color: colors.text }}
          onPress={onCancel}
        />
        <Button
          text="Apply"
          style={[styles.applyButton, { backgroundColor: colors.primary }]}
          textStyle={{ color: "white" }}
          onPress={handleApply}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: "transparent",
    padding: 8,
    gap: 8,
    minWidth: 280,
    maxWidth: 360,
    overflow: "visible",
  },
  section: {
    gap: 8,
  },
  labelRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  label: {
    fontSize: 14,
    fontWeight: "600",
  },
  revertButton: {
    height: 24,
    paddingHorizontal: 8,
    gap: 4,
    borderRadius: 4,
  },
  revertButtonText: {
    fontSize: 11,
    fontWeight: "600",
  },
  originalRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  originalLabel: {
    fontSize: 12,
    width: 60,
  },
  originalValue: {
    fontSize: 13,
    fontWeight: "500",
  },
  overrideRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  overrideLabel: {
    fontSize: 12,
    width: 60,
  },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 10,
    height: 36,
    flex: 1,
    gap: 4,
  },
  input: {
    flex: 1,
    fontSize: 14,
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
  },
  flatLabel: {
    fontSize: 11,
    fontStyle: "italic",
  },
  footer: {
    flexDirection: "row",
    gap: 8,
    justifyContent: "flex-end",
    marginTop: 8,
  },
  cancelButton: {
    height: 36,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  applyButton: {
    height: 36,
    paddingHorizontal: 16,
  },
});
